<script setup lang="ts">
import { computed, useSlots } from 'vue'
import { twMerge } from 'tailwind-merge'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'
import InfoCircleIcon from '@/components/icons/InfoCircleIcon.vue'

const props = withDefaults(defineProps<{
	backgroundClass?: string
	borderClass?: string

	topText?: string
	middleText?: string
	bottomText?: string

	middleImage?: string

	topClass?: string
	middleClass?: string
	bottomClass?: string
	contentClass?: string

	hasInfoIcon?: boolean
}>(), {
  backgroundClass: 'scale-up bg-silver-gradient pulse-shadow-prizes',
  borderClass: 'bg-green-gradient-stroke',
  topText: '',
  middleText: '',
  bottomText: '',
  middleImage: undefined,

  topClass: 'text-white text-base font-semibold -tracking-[0.16px]',
  middleClass: 'text-lg leading-5 font-bold text-white',
  bottomClass: 'uppercase text-clip-green-gradient text-xs font-medium',
  contentClass: '',

  hasInfoIcon: true,
})

const slots = useSlots()
const { addHapticFeedback } = useHapticFeedback()

const hasTop = computed(() => !!slots.top || !!props.topText)
const hasMiddle = computed(() => !!slots.middle || !!props.middleText)
const hasBottom = computed(() => !!slots.bottom || !!props.bottomText)
</script>

<template>
  <div :class="twMerge('flex-1 bg-black rounded-xl w-1/2', backgroundClass)">
    <div :class="twMerge('relative w-full h-full rounded-xl p-0.5', borderClass)">
      <div
        :class="twMerge('bg-black rounded-[10px] w-full h-full px-[14px] py-2 flex flex-col items-center justify-center', contentClass)"
        @click="addHapticFeedback('medium')"
      >
        <div
          v-if="hasTop"
          :class="twMerge('flex items-center', topClass)"
        >
          <slot name="top">
            <span>{{ topText }}</span>
          </slot>
        </div>

        <div
          v-if="hasMiddle"
          :class="twMerge('flex items-center justify-center gap-x-1 text-center', middleClass)"
        >
          <slot name="middle">
            <img
              v-if="middleImage"
              :src="middleImage"
              class="size-6 min-w-6"
            >
            <span class="flex-[0]">{{ middleText }}</span>
          </slot>
        </div>

        <div
          v-if="hasBottom"
          :class="twMerge('mt-1 flex items-center', bottomClass)"
        >
          <slot name="bottom">
            <span>{{ bottomText }}</span>
          </slot>
        </div>
      </div>
      <slot name="info-icon">
        <InfoCircleIcon
          v-if="hasInfoIcon"
          class="absolute bottom-1 right-1 size-4 text-white/[.38]"
        />
      </slot>
    </div>
  </div>
</template>

<style scoped lang="scss">

.scale-up {
	animation: scale-up 0.75s ease infinite alternate;
}

@keyframes scale-up {
	from {
		transform: scale(1);
	}
	to {
		transform: scale(1.03);
	}
}

.bg-green-gradient-stroke {
	background: linear-gradient(
			-45deg,
			rgba(255, 203, 255, 0.77) 22%,
			#6e0e6e 24%,
			#14F1FF 42%,
			#14F1FF 63%,
			rgba(255, 203, 255, 0.77) 77%,
			#ffcbff 82%
	);
	box-shadow: 0px 0px 24px 0px rgba(230, 185, 12, 0.66);
}

.scrollable-container {
	-ms-overflow-style: none; /* Internet Explorer 10+ */
	scrollbar-width: none; /* Firefox */
}

.scrollable-container::-webkit-scrollbar {
	display: none; /* Safari and Chrome */
}

.text-clip-green-gradient {
	background: var(--Blue-Degrade, linear-gradient(180deg, #E2FFA5 0%, #C6F36B 98%));
	background-clip: text;
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
}
</style>

<style lang="scss">
@import "@/assets/styles/challenges";

.text-shadow-default {
	text-shadow: 0px 0px 115.874px rgba(0, 0, 0, 0.50);
}

.gradient-black-fade {
	background: linear-gradient(
			to top,
			rgba(0, 0, 0, 1) 42.5%,
			rgba(0, 0, 0, 0.65) 65.15%,
			rgba(0, 0, 0, 0) 100%
	);
}
</style>
